import {motion, useReducedMotion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import './VenturePage.css';


const framework=[
  ['01','Discover Opportunities','Identify challenges and unmet needs.'],
  ['02','Evaluate Potential','Assess scalability,impact,and long-term viability.'],
  ['03','Build Foundations','Develop systems,partnerships,and infrastructure.'],
  ['04','Execute Strategically','Launch initiatives through disciplined implementation.'],
  ['05','Scale Responsibly','Grow while maintaining quality and sustainability.'],
  ['06','Create Lasting Value','Deliver benefits for businesses,partners,and communities.']
];

const initiatives=[
  ['ProfitYo App','In Development','Technology solutions designed to simplify operations,enhance efficiency,and support business growth.','FiCpu','initiative-blue'],
  ['Financial & Banking Collaborations','Strategic Exploration','Strategic financial partnerships creating greater accessibility,opportunity,and value for entrepreneurs and business owners.','FiGlobe','initiative-gold'],
  ['Business Systems & Automation','Ongoing Development','Scalable systems and operational frameworks that improve efficiency across the wider ecosystem.','FiZap','initiative-slate'],
  ['Strategic Growth Opportunities','Active Exploration','New ventures,partnerships,and business initiatives aligned with our long-term vision.','FiCompass','initiative-purple']
];

const opportunityAreas=[
  ['Artificial Intelligence','Leveraging emerging technologies to enhance productivity,decision-making,and business operations.','FiCpu'],
  ['Technology & Digital Solutions','Building platforms and tools that simplify complex processes and improve user experiences.','FiMonitor'],
  ['Financial Innovation','Exploring solutions that strengthen financial accessibility and business growth.','FiDollarSign'],
  ['Education & Learning','Supporting continuous learning,entrepreneurship,and personal development.','FiBookOpen'],
  ['Business Services','Developing services that empower entrepreneurs and growing organizations.','FiBriefcase'],
  ['Lifestyle & Consumer Brands','Cultivating brands that create meaningful experiences and long-term customer value.','FiShoppingBag']
];

const principles=[
  'Solve meaningful problems',
  'Create genuine value',
  'Support long-term growth',
  'Strengthen our ecosystem',
  'Demonstrate scalability',
  'Encourage innovation',
  'Align with our vision',
  'Create sustainable impact'
];

function FrameworkStep({step,index}) {
  return (
    <motion.article
      className="venture-framework-step"
      initial={{opacity: 0,y: 18}}
      whileInView={{opacity: 1,y: 0}}
      viewport={{once: true,amount: .2}}
      transition={{delay: index * .07}}
    >
      <span>{step[0]}</span>
      <div>
        <h3>{step[1]}</h3>
        <p>{step[2]}</p>
      </div>
      <SafeIcon icon={FiIcons.FiArrowUpRight} />
    </motion.article>
  );
}

function InitiativeCard({initiative,index}) {
  return (
    <motion.article
      className={`venture-initiative-card ${initiative[4]}`}
      initial={{opacity: 0,y: 22}}
      whileInView={{opacity: 1,y: 0}}
      viewport={{once: true,amount: .15}}
      transition={{delay: index * .08}}
      whileHover={{y: -8}}
    >
      <div className="venture-card-top">
        <span className="venture-card-icon">
          <SafeIcon name={initiative[3].replace('Fi','')} />
        </span>
        <span className="venture-status">{initiative[1]}</span>
      </div>
      <span className="venture-card-index">0{index + 1}</span>
      <h3>{initiative[0]}</h3>
      <p>{initiative[2]}</p>
      <span className="venture-card-footer">
        Growth initiative
        <SafeIcon icon={FiIcons.FiArrowUpRight} />
      </span>
    </motion.article>
  );
}

function OpportunityCard({area,index}) {
  return (
    <motion.article
      className="venture-opportunity-card"
      initial={{opacity: 0,y: 18}}
      whileInView={{opacity: 1,y: 0}}
      viewport={{once: true,amount: .15}}
      transition={{delay: index * .06}}
      whileHover={{y: -6}}
    >
      <span className="opportunity-number">0{index + 1}</span>
      <SafeIcon name={area[2].replace('Fi','')} />
      <h3>{area[0]}</h3>
      <p>{area[1]}</p>
    </motion.article>
  );
}

function FutureVentureGraph() {
  const reducedMotion = useReducedMotion();

  const duration = reducedMotion ? 0 : 0.48;
  const sequenceGap = reducedMotion ? 0 : 0.18;

  const nodeVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration
      }
    }
  };

  return (
    <motion.div
      className="venture-future-visual"
      initial="hidden"
      whileInView="visible"
      viewport={{
        once: true,
        amount: 0.35
      }}
      variants={{
        visible: {
          transition: {
            staggerChildren: sequenceGap
          }
        }
      }}
      role="img"
      aria-label="ProfitYo Global System connects ideas, ventures, and impact"
    >
      <div className="venture-future-orbit" />

      <motion.div
        className="future-node future-node-core"
        variants={nodeVariants}
      >
        PROFITYO
        <br />
        GLOBAL SYSTEM
      </motion.div>

      <motion.div
        className="future-node future-node-one"
        variants={nodeVariants}
      >
        IDEAS
      </motion.div>

      <motion.div
        className="future-node future-node-two"
        variants={nodeVariants}
      >
        VENTURES
      </motion.div>

      <motion.div
        className="future-node future-node-three"
        variants={nodeVariants}
      >
        IMPACT
      </motion.div>
    </motion.div>
  );
}

export default function VenturePage() {
  return (
    <div className="venture-page">
      <section className="venture-hero">
        <div className="venture-hero-grid" />
        <div className="venture-hero-orbit venture-orbit-one" />
        <div className="venture-hero-orbit venture-orbit-two" />
        <div className="container venture-hero-layout">
          <motion.div
            className="venture-hero-copy"
            initial={{opacity: 0,x: -25}}
            animate={{opacity: 1,x: 0}}
          >
            <span className="venture-eyebrow">Future ventures</span>
            <h1>
              Building tomorrow&apos;s
              <br />
              <em>opportunities.</em>
            </h1>
            <p>
              ProfitYo Global System is continually identifying opportunities,developing new ventures,forming strategic partnerships,and building the foundations for long-term growth.
            </p>
            <p className="venture-hero-support">
              We believe the future belongs to organizations that continuously innovate,adapt,and create value across technology,finance,entrepreneurship,and emerging industries.
            </p>
          </motion.div>
          <motion.div
            className="venture-hero-visual"
            initial={{opacity: 0,scale: .88}}
            animate={{opacity: 1,scale: 1}}
            transition={{duration: .7}}
          >
            <div className="venture-visual-pulse" />
            <div className="venture-visual-ring venture-ring-a" />
            <div className="venture-visual-ring venture-ring-b" />
            <div className="venture-visual-core">
              <span>THE NEXT</span>
              <strong>CHAPTER</strong>
              <small>IS BEING BUILT</small>
            </div>
            <span className="venture-visual-label venture-label-one">VISION</span>
            <span className="venture-visual-label venture-label-two">INNOVATION</span>
            <span className="venture-visual-label venture-label-three">IMPACT</span>
            <span className="venture-visual-label venture-label-four">GROWTH</span>
          </motion.div>
        </div>
        <div className="venture-scroll-note">
          <span />
          Explore what is next
        </div>
      </section>

      <section className="venture-approach">
        <div className="container">
          <div className="venture-section-heading venture-approach-heading">
            <div>
              <span className="venture-eyebrow">Our approach to building ventures</span>
              <h2>From vision<br /><em>to opportunity.</em></h2>
            </div>
            <p>
              Every venture begins with a problem worth solving and an opportunity worth pursuing. We evaluate possibilities through a long-term lens,focusing on initiatives that strengthen the ecosystem and create meaningful value.
            </p>
          </div>
          <div className="venture-framework">
            {framework.map((step,index)=>(
              <FrameworkStep key={step[0]} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="venture-initiatives">
        <div className="container">
          <div className="venture-section-heading">
            <span className="venture-eyebrow">Current growth initiatives</span>
            <h2>What we are<br /><em>building.</em></h2>
            <p>
              Our current initiatives represent the next layer of growth: practical,strategic,and designed to compound over time.
            </p>
          </div>
          <div className="venture-initiative-grid">
            {initiatives.map((initiative,index)=>(
              <InitiativeCard key={initiative[0]} initiative={initiative} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="venture-opportunities">
        <div className="container">
          <div className="venture-section-heading centered">
            <span className="venture-eyebrow">Areas of future opportunity</span>
            <h2>Expanding<br /><em>beyond today.</em></h2>
            <p>
              As the ecosystem evolves,we continue exploring opportunities with the potential to create meaningful impact and sustainable growth.
            </p>
          </div>
          <div className="venture-opportunity-grid">
            {opportunityAreas.map((area,index)=>(
              <OpportunityCard key={area[0]} area={area} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="venture-philosophy">
        <div className="container venture-philosophy-layout">
          <div>
            <span className="venture-eyebrow">Our investment philosophy</span>
            <h2>What makes an opportunity<br /><em>worth pursuing?</em></h2>
            <p>
              We look beyond immediate returns. The strongest opportunities solve real problems,expand capability,and create a stronger foundation for what comes next.
            </p>
          </div>
          <div className="venture-principles">
            {principles.map((principle,index)=>(
              <motion.div
                key={principle}
                className="venture-principle"
                initial={{opacity: 0,x: 15}}
                whileInView={{opacity: 1,x: 0}}
                viewport={{once: true}}
                transition={{delay: index * .06}}
              >
                <span>0{index + 1}</span>
                <strong>{principle}</strong>
                <SafeIcon icon={FiIcons.FiCheck} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="venture-future">
        <div className="venture-future-lines" />
        <div className="container venture-future-layout">
          <div>
            <span className="venture-eyebrow">The future we envision</span>
            <h2>Building the next<br /><em>generation of ventures.</em></h2>
            <p>
              The future of ProfitYo Global System extends beyond any single company,product,or industry. Our vision is to build an ecosystem of businesses,technologies,partnerships,and opportunities that create value across multiple sectors.
            </p>
            <p>What we build today becomes the foundation for tomorrow&apos;s growth.</p>
          </div>
          <FutureVentureGraph />
        </div>
      </section>

      <section className="venture-closing">
        <div className="container">
          <span className="venture-eyebrow">The next chapter starts here</span>
          <h2>Build with vision.<br /><em>Grow with purpose.</em></h2>
        </div>
      </section>
    </div>
  );
}